/**
 * Query used to search vendor entity by providing the search criteria.
 * Below are the list of services that it talks to:
 * AWSSCMVendorMasterService
 */
import { gql } from '@apollo/client';

import { DCSP, IPS, PAC } from '../../data/BusinessConstants';

export const searchVendorEntityQuery = gql`
  query searchVendorEntity($query: VendorSearchQuery!, $nextToken: String, $maxResults: Int) {
    searchVendorEntity(query: $query, nextToken: $nextToken, maxResults: $maxResults) {
      vendorEntitySummaryList {
        id
        friendlyName
        name
        parentVendorEntityId
        productLines {
          categoryName
          departmentName
          qualificationStatus
        }
        address {
          id
          addressLine1
          addressLine2
          addressLine3
          city
          state
          countryCode
          postalCode
        }
      }
      nextToken
    }
  }
`;

export const SearchVendorQuery = (
  searchField: string,
  propertyCondition: PropertyCondition[],
  productLinesConditions: ProductLineCondition[]
) => {
  return {
    maxResults: 200,
    query: {
      conditions: {
        classId: {
          isOneOf: ['BusinessEntityClassType']
        },
        combineWith: 'AND',
        propertyCombineWith: 'AND',
        propertyConditions: propertyCondition,
        searchField: {
          combineWith: 'OR',
          fuzzyMatch: searchField,
          prefixMatch: searchField
        },
        productLinesConditions: productLinesConditions,
        productLinesCombineWith: 'AND'
      }
    }
  };
};

// Ref: https://tiny.amazon.com/wywtm547/tinyamaz10v1AWSSmainxml
export enum QualificationStatus {
  NEW = 'NEW',
  PRE_QUALIFIED = 'PRE_QUALIFIED',
  QUALIFICATION_IN_PROGRESS = 'QUALIFICATION_IN_PROGRESS',
  NON_QUALIFIED = 'NON_QUALIFIED',
  QUALIFIED = 'QUALIFIED',
  VENDOR_ENGAGEMENT_APPROVED = 'VENDOR_ENGAGEMENT_APPROVED',
}

/* 
  These departmentNames are used in the searchQuery for filtering vendors from VendorService.
  Ensure that these departmentNames are same as the ones in VendorService.
*/
export enum DepartmentName {
  PAC = 'Power and Cooling',
  DCSP = 'Data Center Services Procurement',
  IPS = 'IPS',
}

export interface SearchVendorEntityResult {
  [x: string]: any;
  vendorEntitySummaryList: VendorSummary[];
  nextToken?: string;
}

export interface VendorSummary {
  id: string;
  friendlyName?: string;
  address?: Address;
  name?: string;
  parentVendorEntityId?: string;
  productLines?: ProductLine[];
}

export interface Address {
  id: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  state: string;
  countryCode: string;
  postalCode: string;
}

export const PACPropertyCondition: PropertyCondition[] = [
  {
    propertyConditionType: {
      isOneOfPhraseMatch: [DepartmentName.PAC]
    },
    propertyName: 'productLines'
  }
]
export interface PropertyCondition {
  propertyConditionType: {
    isOneOfPhraseMatch: string[];
  };
  propertyName: string;
}

//TODO: Move all the VS related interfaces to a common file
export interface ProductLine {
  categoryName: string;
  departmentName: string;
  qualificationStatus: QualificationStatus;
}

export interface ProductLineCondition {
  categoryName: {
    isOneOfPhraseMatch: string[] | null;
  };
  departmentName: {
    isOneOfPhraseMatch: string[] | null;
  };
  qualificationStatus?: {
    isOneOf: string[];
  };
}

export const getDepartmentNameForBusinessId = (
  businessId: string
): DepartmentName | null => {
  switch (businessId) {
    case DCSP:
      return DepartmentName.DCSP;
    case PAC:
      return DepartmentName.PAC;
    case IPS:
      return DepartmentName.IPS;
    default:
      return null;
  }
}

export const getProductLineFilter = (
  categoryName: string | null,
  businessId: string
): ProductLineCondition[]  => {
  const departmentName: DepartmentName | null = getDepartmentNameForBusinessId(businessId);
  return [
    {
      categoryName: {
        isOneOfPhraseMatch: categoryName? [categoryName]: null
      }, 
      departmentName: {
        isOneOfPhraseMatch: departmentName? [departmentName]: null
      }
    }
  ];
}

export const getProductLineFilterWithMultiCategories = (
  categories: string[] | null | undefined,
  businessId: string
): ProductLineCondition[]  => {
  const departmentName: DepartmentName | null = getDepartmentNameForBusinessId(businessId);
  return [
    {
      categoryName: {
        isOneOfPhraseMatch: categories? categories: null
      },
      departmentName: {
        isOneOfPhraseMatch: departmentName? [departmentName]: null
      }
    }
  ];
}